import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from './store';
import { apiString as API_URL } from '../utils/constants';
import axios from 'axios';
import b64toBlob from 'b64-to-blob';
import { setDownLoading, setIsLoading } from './shared.slice';
import { dateFormatter } from '../utils/timeFormat';
import { OnboardingCallReport } from '../Models/AdminOnboarding/OnboardingCallReport';
import { activityOptions } from './onboardingWorkflow.slice';
import { MemberStatusEnum } from '../Enum/MemberStatusEnum';

interface OnboardingMemberReportState {
    exporting: boolean;
    isTableFetching: boolean;
    memberStatus: MemberStatusEnum[];
    onboardingReportStartDate: Date;
    onboardingReportEndDate: Date;
    onboardingRowData: unknown[];
    selectedHealthPlan: string;
    selectedNoteType: string[];
    selectedOnboarder: string;
    selectedPopulation: number;
    unreachablePhonesData: unknown[];
}

const initialState: OnboardingMemberReportState = {
    exporting: false,
    isTableFetching: false,
    onboardingReportStartDate: new Date(),
    onboardingReportEndDate: new Date(),
    onboardingRowData: [],
    selectedHealthPlan: '-1',
    selectedNoteType: [activityOptions.ATTEMPTED_CALL],
    selectedOnboarder: '-1',
    selectedPopulation: -1,
    memberStatus: [MemberStatusEnum.Active],
    unreachablePhonesData: [],
};

const onboardingMemberReport = createSlice({
    name: 'onboardingMemberReport',
    initialState,
    reducers: {
        setExporting(state, action: PayloadAction<boolean>) {
            state.exporting = action.payload;
        },
        setIsTableFetching(state, action: PayloadAction<boolean>) {
            state.isTableFetching = action.payload;
        },
        setMemberStatus(state, action: PayloadAction<MemberStatusEnum[]>) {
            state.memberStatus = action.payload;
        },
        setOnboardingStartDate(state, action: PayloadAction<Date>) {
            state.onboardingReportStartDate = action.payload;
        },
        setOnboardingEndDate(state, action: PayloadAction<Date>) {
            state.onboardingReportEndDate = action.payload;
        },
        setOnboardingRowData(state, action: PayloadAction<unknown[]>) {
            state.onboardingRowData = action.payload;
        },
        setSelectedHealthPlan(state, action: PayloadAction<string>) {
            state.selectedHealthPlan = action.payload;
        },
        setSelectedNoteType(state, action: PayloadAction<string | string[]>) {
            state.selectedNoteType = Array.isArray(action.payload) ? action.payload : [action.payload];
        },
        setSelectedOnboarder(state, action: PayloadAction<string>) {
            state.selectedOnboarder = action.payload;
        },
        setSelectedPopulation(state, action: PayloadAction<number>) {
            state.selectedPopulation = action.payload;
        },
        setUnreachablePhonesData(state, action: PayloadAction<unknown[]>) {
            state.unreachablePhonesData = action.payload;
        },
    },
});

export const {
    setExporting,
    setIsTableFetching,
    setMemberStatus,
    setOnboardingStartDate,
    setOnboardingEndDate,
    setOnboardingRowData,
    setSelectedHealthPlan,
    setSelectedNoteType,
    setSelectedOnboarder,
    setSelectedPopulation,
    setUnreachablePhonesData,
} = onboardingMemberReport.actions;

// Utility functions
const formatExportDate = (): string => {
    const date = new Date();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}${dd}${yyyy}`;
};

const downloadFile = (blob: Blob, fileName: string): void => {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.setAttribute('download', fileName);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
};

export const fetchOnboardingMemberReport =
    (startDate: Date, endDate: Date): AppThunk =>
    async (dispatch) => {
        try {
            dispatch(setIsLoading(true));
            const response = await axios.get(`${API_URL}/onboardingreport/getonboardingreport?start=${dateFormatter(startDate)}&end=${dateFormatter(endDate)}`);
            dispatch(setOnboardingRowData(response.data));
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const fetchOnboardingCallsReport =
    (onboardingCallReport: OnboardingCallReport): AppThunk =>
    async (dispatch) => {
        dispatch(setIsTableFetching(true));
        try {
            const response = await axios.post(`${API_URL}/onboardingreport/getonboardingcallsreport`, onboardingCallReport);
            dispatch(setUnreachablePhonesData(response.data));
        } catch (error) {
            console.error('Error fetching unreachable phones report:', error);
        } finally {
            dispatch(setIsTableFetching(false));
        }
    };

export const exportOnboardingMemberReport =
    (startDate: Date, endDate: Date): AppThunk =>
    async (dispatch) => {
        try {
            dispatch(setDownLoading(true));
            const startDateStr = dateFormatter(startDate);
            const endDateStr = dateFormatter(endDate);
            const url = `${API_URL}/onboardingreport/exportonboardingreport?start=${startDateStr}&end=${endDateStr}`;
            const response = await axios.get(url);
            const blob = b64toBlob(response.data.base64String, 'text/csv');
            const fileName = `OnboardingReport - ${formatExportDate()}.xlsx`;
            downloadFile(blob, fileName);
        } catch (error) {
            console.error('Error exporting onboarding report:', error);
        } finally {
            dispatch(setDownLoading(false));
        }
    };

export default onboardingMemberReport.reducer;
